import { CSSProperties } from "react";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
  // TutorialStyleConfig,
  FeedbackWorkflowStyleConfig,
  FeedbackWorkflowThemeConfig,
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
} from "./feedbackWorkflow.types";
import { UiPropsInterface } from "../modular/types";
import { hexToGlassColor } from "../../helphub/component/chatcomponents/helperfunction";
import { adjustColor } from "./ulils";
import { extractGradientColors } from "../../../../general";

class FeedbackWorkflowThemeManager {
  themeConfig: FeedbackWorkflowThemeConfig;
  styleConfig: FeedbackWorkflowStyleConfig;
  questThemeData: PartialQuestThemeData;
  brandTheme: PartialBrandTheme;
  UiProps: UiPropsInterface | null;

  constructor(
    themeConfig: FeedbackWorkflowThemeConfig = {},
    styleConfig: FeedbackWorkflowStyleConfig = {},
    questThemeData: PartialQuestThemeData = DEFAULT_QUEST_THEME,
    brandTheme: PartialBrandTheme = DEFAULT_BRAND_THEME,
    UiProps: UiPropsInterface | null
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = { ...DEFAULT_QUEST_THEME, ...questThemeData };
    this.brandTheme = { ...DEFAULT_BRAND_THEME, ...brandTheme };
    this.UiProps = UiProps;
  }

  getStyleValueAsString(path: string, defaultValue: string = ""): string {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return typeof value === "string" ? value : defaultValue;
  }

  getStyleValueAsObject<T>(path: string, defaultValue: T): T {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return (typeof value === "object" ? value : defaultValue) as T;
  }

  // Modal
  getModalStyles(): CSSProperties {
    return {
      borderRadius: this.getBorderRadius(),
      ...this.styleConfig?.Modal,
    };
  }

  // Form
  getFormStyles(): CSSProperties {
    return {
      background: this.getBackgroundColor(),
      height: this.styleConfig?.Form?.height || "auto",
      borderRadius: this.getBorderRadius(),
      fontFamily: this.getFontFamily(),
      ...this.styleConfig?.Form,
    };
  }

  // Heading Style
  getHeadingStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor ||
        "#000000",
      ...this.styleConfig?.Heading,
    };
  }

  // description Style
  getDescriptionStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.secondaryColor ||
        this.themeConfig?.secondaryColor ||
        "#666666",
      ...this.styleConfig?.Description,
    };
  }

  // done
  getTopBarStyles() {
    return {
      headingStyle: {
        color:
          this.getStyleValueAsString("Heading.color") ||
          this.brandTheme?.titleColor ||
          this.brandTheme?.primaryColor ||
          this.themeConfig?.primaryColor ||
          "#000000",
        ...this.styleConfig?.Heading,
      },
      descriptionStyle: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.contentColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
        ...this.styleConfig?.Description,
      },
      topbarStyle: {
        ...this.styleConfig?.TopBar,
      },
    };
  }

  // done
  getPrimaryButtonStyles() {
    return {
      background:
        this.styleConfig?.PrimaryButton?.background ||
        this.styleConfig?.PrimaryButton?.backgroundColor ||
        this.questThemeData?.buttonColor ||
        this.questThemeData?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.brandTheme?.primaryButton,
      borderColor: this.styleConfig?.PrimaryButton || this.questThemeData?.accentColor,
      ...this.styleConfig.PrimaryButton,
    };
  }

  // done
  getStarStyles() {
    return {
      Style: this.styleConfig?.Star?.Style,
      PrimaryColor: this.styleConfig?.Star?.PrimaryColor,
      SecondaryColor: this.styleConfig?.Star?.SecondaryColor,
      Size: this.styleConfig?.Star?.Size,
    };
  }

  // done
  getLabelStyles(): CSSProperties {
    return {
      ...this.styleConfig?.Label,
      color:
        this.styleConfig?.Label?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.primaryColor,
    };
  }

  getInputStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Input?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Input,
    };
  }

  getEmailText(): string {
    return this.styleConfig?.EmailError?.text || "This is not a valid email";
  }

  getGeneralFeedbackEmailText(feedbackType: string): string {
    if (feedbackType === "ReportBug") {
      return (
        this.UiProps?.styleConfig?.EmailError?.text ||
        "This is not a valid email"
      );
    } else if (feedbackType === "RequestFeature") {
      return (
        this.UiProps?.styleConfig?.EmailError?.text ||
        "This is not a valid email"
      );
    } else if (feedbackType === "GeneralFeedback") {
      return (
        this.UiProps?.styleConfig?.EmailError?.text ||
        "This is not a valid email"
      );
    }
  }

  getGeneralFeedbackEmailErrorStyles(feedbackType: string): CSSProperties {
    if (feedbackType === "ReportBug") {
      return {
        ...this.UiProps?.styleConfig?.EmailError?.errorStyle,
      };
    } else if (feedbackType === "RequestFeature") {
      return {
        ...this.UiProps?.styleConfig?.EmailError?.errorStyle,
      };
    } else if (feedbackType === "GeneralFeedback") {
      return {
        ...this.UiProps?.styleConfig?.EmailError?.errorStyle,
      };
    }
  }

  // done
  getGeneralFeedbackTextAreaStylesd(feedbackType: string): CSSProperties {
    if (feedbackType === "ReportBug") {
      return {
        borderColor:
          this.UiProps?.styleConfig?.TextArea?.borderColor,
        color: this.UiProps?.styleConfig?.TextArea?.color || this.brandTheme?.titleColor,
        ...this.UiProps?.styleConfig?.TextArea,
        ...this.styleConfig?.TextArea,
      };
    } else if (feedbackType === "RequestFeature") {
      return {
        borderColor:
          this.UiProps?.styleConfig?.TextArea?.borderColor,
        color: this.UiProps?.styleConfig?.TextArea?.color || this.brandTheme?.titleColor,
        ...this.UiProps?.styleConfig?.TextArea,
        ...this.styleConfig?.TextArea,
      };
    } else if (feedbackType === "GeneralFeedback") {
      return {
        borderColor:
          this.UiProps?.styleConfig?.TextArea?.borderColor,
        color: this.UiProps?.styleConfig?.TextArea?.color || this.brandTheme?.titleColor,
        ...this.UiProps?.styleConfig?.TextArea,
        ...this.styleConfig?.TextArea,
      };
    }
  }

  getStepStyles() {
    return {
      step: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.secondaryColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
      },
      title: {
        color:
          this.getStyleValueAsString("Heading.color") ||
          this.brandTheme?.primaryColor ||
          this.themeConfig?.primaryColor ||
          "#000000",
      },
      description: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.secondaryColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
      },
    };
  }

  ensureBoolean(value: boolean | undefined): boolean {
    return Boolean(value);
  }

  getProgressStyles(isCompleted: boolean | undefined) {
    const completed = this.ensureBoolean(isCompleted);

    return {
      imageCont: {
        background: completed ? "#01ff0111" : "#FBFBFB",
      },
      connector: {
        background: completed ? "#73DCA7" : "#EFEFEF",
      },
    };
  }

  // done
  getFooterStyles() {
    return {
      FooterStyle: {
        background:
          this.styleConfig?.Footer?.FooterStyle?.background ||
          this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.getBackgroundColor(),
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      FooterTextStyle: {
        ...this.styleConfig?.Footer?.FooterTextStyle,
      },
      FooterIcon: {
        ...this.styleConfig?.Footer?.FooterIcon,
      },
      FooterText: this.styleConfig?.Footer?.FooterText,
      FooterLink: this.styleConfig?.Footer?.FooterLink,
    };
  }

  // done
  getListHoverCardStyle(): CSSProperties {
    return {
      background: this.styleConfig?.listHover?.background || "#FBFBFB",
      ...this.styleConfig?.Card,
    };
  }

  // done
  getListHoverIconStyle(): CSSProperties {
    let color = this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#F4EBFF";
    if (color?.includes("gradient")) {
      color = extractGradientColors(color)
    }
    return {
      background: this.styleConfig?.listHover?.iconBackground || hexToGlassColor(color, 0.1),
    };
  }

  getListHoverIconDefaultStyle(): CSSProperties {
    return {
      background: this.styleConfig?.listHover?.defaultIconBackground || "#FBFBFB",
    };
  }

  getListHoverIcon(): CSSProperties {
    return {
      ...this.styleConfig?.listHover?.Icon,
    };
  }

  getListHoverSvg(): CSSProperties {
    return {
      color:
        (this.styleConfig?.listHover?.iconColor?.includes("gradient") ? extractGradientColors(this.styleConfig?.listHover?.iconColor) : this.styleConfig?.listHover?.iconColor) || (this.brandTheme?.primaryColor?.includes("gradient") ? extractGradientColors(this.brandTheme?.primaryColor) : this.brandTheme?.primaryColor) || (this.questThemeData?.accentColor?.includes("gradient") ? extractGradientColors(this.questThemeData?.accentColor) : this.questThemeData?.accentColor) || adjustColor(this.questThemeData?.accentColor, -40),
    };
  }
  // done
  getListHoverIconDetailStyle(): {
    color: string;
    size: number | string | CSSProperties;
  } {
    return {
      color:
        this.styleConfig?.listHover?.iconColor ||
        this.styleConfig?.listHover?.Icon?.color ||
        "#9035FF",
      size: this.styleConfig?.listHover?.Icon,
    };
  }

  // done
  getListHoverHeadingStyle1(): CSSProperties {
    return {
      color:
        this.styleConfig?.listHover?.Heading ||
        this.styleConfig?.listHeading?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
    };
  }

  // done
  getListHoverHeadingStyle2(): CSSProperties {
    return {
      color:
        this.styleConfig?.listHeading?.color ||
        this.styleConfig?.Heading?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
    };
  }

  // done
  getListHeadingStyle(): CSSProperties {
    return {
      ...this.styleConfig?.listHeading,
    };
  }

  // done
  getListHoverDescriptionStyle1(): CSSProperties {
    return {
      color:
        this.styleConfig?.listHover?.Description ||
        this.styleConfig?.listDescription?.color ||
        this.styleConfig?.Description?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
    };
  }

  // done
  getListHoverDescriptionStyle2(): CSSProperties {
    return {
      color:
        this.styleConfig?.listDescription?.color ||
        this.styleConfig?.Description?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
    };
  }

  // done
  getListDescriptionStyle(): CSSProperties {
    return {
      ...this.styleConfig?.listDescription,
    };
  }

  // done
  getGeneralFeedbackInputStyle(feedbackType: string): CSSProperties {
    if (feedbackType === "ReportBug") {
      return {
        ...this.UiProps?.styleConfig?.Input,
        ...this.styleConfig?.Input,
      };
    } else if (feedbackType === "RequestFeature") {
      return {
        ...this.UiProps?.styleConfig?.Input,
        ...this.styleConfig?.Input,
      };
    } else if (feedbackType === "GeneralFeedback") {
      return {
        ...this.UiProps?.styleConfig?.Input,
        ...this.styleConfig?.Input,
      };
    }
    return {
      ...this.UiProps?.styleConfig?.Input,
      ...this.styleConfig?.Input,
    };
  }
  // done
  getGeneralFeedbackPrimaryButtonStyle(feedbackType: string): CSSProperties {
    if (feedbackType === "ReportBug") {
      return {
        ...this.UiProps?.styleConfig?.PrimaryButton,
      };
    } else if (feedbackType === "RequestFeature") {
      return {
        ...this.UiProps?.styleConfig?.PrimaryButton,
      };
    } else if (feedbackType === "GeneralFeedback") {
      return {
        ...this.UiProps?.styleConfig?.PrimaryButton,
      };
    }
  }

  // done
  getGeneralFeedbackInputFormColor(): string {
    return (
      this.UiProps?.styleConfig?.Input?.color ||
      this.UiProps?.styleConfig?.Form?.color ||
      this.brandTheme?.primaryColor ||
      this.themeConfig?.primaryColor ||
      "#666666"
    );
  }

  // donme
  getThanksPopUpBackgroundStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.ThanksPopup?.Style?.background ||
        this.styleConfig?.ThanksPopup?.Style?.backgroundColor ||
        this.getBackgroundColor(),

      height:
        this.styleConfig?.ThanksPopup?.Style?.height ||
        this.styleConfig?.Form?.height ||
        "auto",
      borderRadius: this.getBorderRadius(),
      fontFamily: this.getFontFamily(),

      ...this.styleConfig?.ThanksPopup?.Style,
    };
  }

  getThanksPopUbIconBackgroundStyle(): string {
    return this.styleConfig?.ThanksPopup?.Icon?.backgroundColor || extractGradientColors(this.brandTheme?.primaryColor) || extractGradientColors(this.questThemeData?.accentColor);
  }

  getThanksPopUpHeadingStyle(): CSSProperties {
    return {
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.ThanksPopup?.Heading,
    };
  }
  getThanksPopUpDescriptionStyle(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.ThanksPopup?.Description,
    };
  }

  getThanksPopUpSecondaryBtnBackground(): CSSProperties {
    return {
      background:
        this.styleConfig?.SecondaryButton?.background,
      ...this.styleConfig?.SecondaryButton,
    };
  }
  getThanksPopUpFooter(): boolean {
    return this.styleConfig?.ThanksPopup?.ShowFooter;
  }

  getBackgroundColor(): string {
    return (
      this.getStyleValueAsString("Form.backgroundColor") ||
      this.getStyleValueAsString("Form.background") ||
      this.brandTheme?.background ||
      this.themeConfig?.backgroundColor
    );
  }

  getFontFamily(): string {
    return (
      this.brandTheme?.fontFamily ||
      this.themeConfig?.fontFamily ||
      "'Figtree', sans-serif"
    );
  }

  getBorderRadius(): string {
    return (
      this.getStyleValueAsString("Form.borderRadius") ||
      this.questThemeData?.borderRadius ||
      this.brandTheme?.borderRadius ||
      "8px"
    );
  }

  // Common style getters
  getPrimaryColor(): string {
    return (
      this.getStyleValueAsString("Heading.color") ||
      this.brandTheme?.primaryColor ||
      this.themeConfig?.primaryColor ||
      "#000000"
    );
  }

  getSecondaryColor(): string {
    return (
      this.getStyleValueAsString("Description.color") ||
      this.brandTheme?.secondaryColor ||
      this.themeConfig?.secondaryColor ||
      "#666666"
    );
  }

  // Update methods
  updateThemeConfig(newConfig: Partial<FeedbackWorkflowThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<FeedbackWorkflowStyleConfig>): void {
    this.styleConfig = {
      ...this.styleConfig,
      ...newConfig,
      Footer: {
        ...this.styleConfig?.Footer,
        ...newConfig?.Footer,
      },
    };
  }

  updateQuestTheme(newTheme: PartialQuestThemeData): void {
    this.questThemeData = {
      ...this.questThemeData,
      ...newTheme,
    };
  }

  updateBrandTheme(newTheme: PartialBrandTheme): void {
    this.brandTheme = {
      ...this.brandTheme,
      ...newTheme,
    };
  }

  mergeStyles(
    baseStyles: CSSProperties,
    overrideStyles?: CSSProperties
  ): CSSProperties {
    return {
      ...baseStyles,
      ...(overrideStyles || {}),
    };
  }

  getComputedStyle(
    path: string,
    defaultStyles: CSSProperties = {}
  ): CSSProperties {
    return this.mergeStyles(
      defaultStyles,
      this.getStyleValueAsObject(path, {})
    );
  }
}

export default FeedbackWorkflowThemeManager;
